import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { LIFE_CATS } from "../../lib/constants";
import { freqLabel } from "../../lib/format";
import { Icon, Lead, List, ListRow, Pill, SectionHeader, EmptyState, Sheet } from "../../components/ui/ds";
import { Glyph } from "../../components/ui";
import { st } from "../../styles.js";
import { AddCard } from "../../components/forms/AddCard";

// ─────────────────────────────────────────────────────────────────────────────
//  KNIŽNICA NÁVYKOV (v38)
//
//  Nahradila „Šablóny návykov". Obsah tvorí VÝHRADNE majiteľka (rovnaký princíp ako
//  globálne výzvy) — vlastné šablóny ani ich zdieľanie tu už nie sú; na produkcii si
//  vlastnú šablónu nevytvoril ani jeden človek, takže sa nikoho nedotklo.
//
//  KDE ŽIJE: otvára sa z FAB „+" (speed-dial), teda presne tam, kde človek zakladá návyk.
//  Nový tab v spodnej lište zámerne NEPRIBUDOL — má 4 sloty a majiteľka nechce natlačené
//  obrazovky (CLAUDE.md §6, dizajnové preferencie).
//
//  ŤUKNUTIE NEPRIDÁ NÁVYK: otvorí bežný formulár `AddCard` s predvyplnenými hodnotami,
//  zúžený propom `fields` na to, čo si má človek doladiť — názov, špecifikáciu, náročnosť,
//  frekvenciu, trvanie (časovač) a upozornenie. Až „Pridať" návyk vytvorí, cez to isté
//  `POST /api/tasks` ako všade inde (žiadny druhý spôsob vzniku návyku).
//
//  ÚPRAVY SA DO KNIŽNICE NEVRACAJÚ: `picked` je lokálny stav tejto obrazovky a formulár
//  volá výhradne `POST /api/tasks`. Knižnica má API len na čítanie (`GET /api/habit-library`),
//  písať do nej vie jedine majiteľka cez `scripts/habit_library_admin.py`.
// ─────────────────────────────────────────────────────────────────────────────

// Práve tieto polia si smie človek pred pridaním zmeniť (zadanie majiteľky). Pôvodne to boli
// štyri; od v32 sa dá upraviť aj názov a špecifikácia — návrh je predloha, nie diktát.
// Formulár má ostať KRÁTKY, takže zvyšok (kategória, skupina, trvanie behu, verejný progres,
// „len dnes") sa nezobrazuje a berie sa z návrhu. `ai` v zozname zámerne NIE JE: iskrička
// „vymyslieť celý návyk" by práve vybraný kurátorský návrh prepísala na niečo iné. Ikonu si
// naopak zmeniť dá — je naviazaná na pole názvu (`show("name")` v `AddCard`) a k prepísanému
// názvu sedieť nemusí.
const LIBRARY_FIELDS = ["name", "spec", "difficulty", "recur", "timer", "remind"];

// „15 min" / „1 min 30 s" — trvanie časovača do meta riadku.
function timerLabel(sec) {
  if (!sec) return "";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  if (m && s) return `${m} min ${s} s`;
  return m ? `${m} min` : `${s} s`;
}

export function HabitLibrarySheet({ onClose, reload, routines, lite }) {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [cat, setCat] = useState("");          // "" = všetky kategórie
  const [picked, setPicked] = useState(null);  // vybraný návrh → formulár pred pridaním

  const load = useCallback(async () => {
    try { const d = await api("/api/habit-library"); setItems(d.items || []); }
    catch (e) { setErr(e.message); setItems([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Kategórie sa berú z LIFE_CATS (jediná sada v appke) a zúžia sa na tie, v ktorých
  // knižnica naozaj niečo má — prázdna pilulka by len zaberala miesto.
  const groups = useMemo(() => {
    const by = {};
    (items || []).forEach((it) => { (by[it.life_cat] = by[it.life_cat] || []).push(it); });
    return LIFE_CATS.filter((c) => by[c.key] && by[c.key].length)
      .map((c) => ({ ...c, items: by[c.key] }));
  }, [items]);
  const shown = cat ? groups.filter((g) => g.key === cat) : groups;

  return (
    <>
      <Sheet title="Knižnica návykov" subtitle="Vyber si návrh a nastav si ho po svojom" icon="book" onClose={onClose}>
        {err && <div style={st.err}>{err}</div>}
        {items === null ? (
          <div className="bz-lib__loading"><div className="spin" /></div>
        ) : !groups.length ? (
          <EmptyState icon="book" title="Knižnica je zatiaľ prázdna"
            desc={'Návyky sem pridáva autorka aplikácie. Zatiaľ si vytvor vlastný cez „+".'} />
        ) : (
          <>
            <div className="bz-lib__filter">
              <Pill on={!cat} onClick={() => setCat("")}>Všetko</Pill>
              {groups.map((g) => (
                <Pill key={g.key} on={cat === g.key} onClick={() => setCat(cat === g.key ? "" : g.key)}>
                  <Icon name={g.icon} size={15} color="currentColor" />{g.label}
                </Pill>
              ))}
            </div>
            {shown.map((g) => (
              <div key={g.key}>
                <SectionHeader label={g.full} icon={g.icon} count={g.items.length} />
                <List>
                  {g.items.map((it) => (
                    <ListRow key={it.id} onClick={() => setPicked(it)}
                      lead={<Lead tone="brand"><Glyph icon={it.icon} size={20} /></Lead>}
                      title={it.label}
                      meta={[it.desc, freqLabel(it), timerLabel(it.timer_sec)].filter(Boolean).join(" · ")}
                      trailing={<Icon name="chevron" size={18} color="var(--neutral-text-subtle)" />} />
                  ))}
                </List>
              </div>
            ))}
          </>
        )}
      </Sheet>
      {picked && (
        // Nadpis už NIE JE názov návrhu: ten je teraz v editovateľnom poli hneď pod ním, takže
        // by sa zdvojoval — a po premenovaní by dokonca klamal. Podnadpis preto drží PÔVOD
        // (z ktorého návrhu knižnice sa vychádza) a nemení sa s tým, čo si človek napíše.
        <AddCard kind="habit" open initial={picked} fields={LIBRARY_FIELDS}
          title="Pridať návyk" subtitle={`Z knižnice: ${picked.label}`}
          routines={routines} lite={lite}
          onOpenChange={(v) => { if (!v) setPicked(null); }}
          onCancel={() => setPicked(null)}
          onAdded={() => { setPicked(null); onClose(); reload(); }} />
      )}
    </>
  );
}
